/**
 * VM-15 — Abschläge (Installment Plans)
 *
 * Verwaltet wiederkehrende Zahlungen. Zwei Kacheln:
 *  1. Vorschläge (aus Bank-Buchungs-Erkennung)
 *  2. Aktive Abschlagspläne
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { formatEUR, formatDate } from "@/lib/constants";
import { toast } from "sonner";
import {
  Receipt, Plus, MagnifyingGlass, Trash, PencilSimple, Warning,
  Lightning, Sparkle, CheckCircle,
} from "@phosphor-icons/react";

const KIND_LABEL = {
  utility: "Versorger (Vermieter zahlt)",
  tenant_advance: "Mieter-Vorauszahlung",
};
const FREQ_LABEL = { monthly: "monatlich", quarterly: "quartalsweise", yearly: "jährlich" };
const FREQ_MONTHS = { monthly: 1, quarterly: 3, yearly: 12 };

/**
 * 2026-07 — Health-Status eines Abschlagsplans anhand der letzten Buchung.
 *  - unbekannt: keine Buchungen zugeordnet → grau
 *  - ok: letzte Buchung liegt innerhalb der erwarteten Frequenz + 10 Tage Karenz → grün
 *  - fällig: erwarteter Zeitraum überschritten aber < doppelte Frequenz → amber
 *  - überfällig: > doppelte Frequenz seit letzter Buchung → rot
 */
function computeHealth(plan) {
  if (!plan.last_booking_date || !plan.tx_count) {
    return { key: "unknown", label: "keine Buchungen", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  }
  const freqMonths = FREQ_MONTHS[plan.frequency] || 1;
  const last = new Date(plan.last_booking_date);
  const now = new Date();
  const daysSince = Math.floor((now - last) / (1000 * 60 * 60 * 24));
  const graceDays = Math.round(freqMonths * 30 + 10);
  if (daysSince <= graceDays) {
    return { key: "ok", label: "aktuell", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  }
  if (daysSince <= graceDays * 2) {
    return { key: "due", label: "fällig", cls: "bg-amber-100 text-amber-800 border-amber-200" };
  }
  return { key: "overdue", label: "überfällig", cls: "bg-rose-100 text-rose-800 border-rose-200" };
}

export default function Abschlaege() {
  const [plans, setPlans] = useState([]);
  const [properties, setProperties] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanCount, setScanCount] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingFromProposal, setCreatingFromProposal] = useState(null);
  const [search, setSearch] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);

  const load = async () => {
    const [p, pr] = await Promise.all([
      api.get("/installment-plans"),
      api.get("/properties"),
    ]);
    setPlans(p.data || []);
    setProperties(pr.data || []);
  };

  // 2026-07 — Auto-Detect beim Öffnen der Seite (leise, ohne Toast, damit User
  // sofort Vorschläge sieht). Nur beim ersten Mount, danach nur manuell.
  useEffect(() => {
    (async () => {
      await load();
      try {
        const { data } = await api.post("/installment-plans/detect");
        setProposals(data.proposals || []);
        setScanCount(data.scanned_transactions);
      } catch {
        /* silent — user kann per Button erneut versuchen */
      } finally {
        setInitialLoad(false);
      }
    })();
  }, []);

  const detect = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/installment-plans/detect");
      setProposals(data.proposals || []);
      setScanCount(data.scanned_transactions);
      if (!data.proposals?.length) toast.info(`${data.scanned_transactions} Buchungen gescannt — keine neuen Muster erkannt.`);
      else toast.success(`${data.proposals.length} Vorschlag${data.proposals.length === 1 ? "" : "e"} erkannt.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler bei Erkennung"); }
    finally { setScanning(false); }
  };

  const deletePlan = async (id) => {
    if (!await window._domivusConfirm("Abschlagsplan löschen? Zugeordnete Buchungen werden entkoppelt.")) return;
    try {
      await api.delete(`/installment-plans/${id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const filtered = useMemo(() => {
    if (!search) return plans;
    const q = search.toLowerCase();
    return plans.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.counterparty_name || "").toLowerCase().includes(q) ||
      (p.counterparty_iban || "").toLowerCase().includes(q)
    );
  }, [plans, search]);

  // 2026-07 — Health-Rollup: Anzahl fälliger + überfälliger Pläne für Header-Hinweis
  const healthCounts = useMemo(() => {
    const counts = { overdue: 0, due: 0, ok: 0, unknown: 0 };
    for (const p of plans) counts[computeHealth(p).key] += 1;
    return counts;
  }, [plans]);

  return (
    <div className="space-y-6" data-testid="abschlaege-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Receipt size={22} /> Abschläge
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Wiederkehrende Zahlungen — Versorger (Strom, Wasser, Gas) und Mieter-Vorauszahlungen.
            Der Algorithmus erkennt gleichbleibende Buchungsmuster automatisch.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={detect} disabled={scanning} data-testid="detect-btn" className="gap-1">
            <Sparkle size={14} weight="fill" /> {scanning ? "Scanne…" : "Muster erkennen"}
          </Button>
          <Button className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setCreating(true)} data-testid="create-plan-btn">
            <Plus size={14} /> Neuer Abschlagsplan
          </Button>
        </div>
      </div>

      {/* 2026-07 — Health-Übersicht: prominenter Hinweis bei fälligen/überfälligen Plänen */}
      {plans.length > 0 && (healthCounts.overdue > 0 || healthCounts.due > 0) && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="health-summary">
          {healthCounts.overdue > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-rose-50 border-rose-200 text-rose-800 font-medium">
              <Warning size={12} weight="fill" /> {healthCounts.overdue} überfällig
            </span>
          )}
          {healthCounts.due > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-amber-50 border-amber-200 text-amber-800 font-medium">
              <Warning size={12} weight="fill" /> {healthCounts.due} fällig
            </span>
          )}
          {healthCounts.ok > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-emerald-50 border-emerald-200 text-emerald-800">
              <CheckCircle size={12} weight="fill" /> {healthCounts.ok} aktuell
            </span>
          )}
          {healthCounts.unknown > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-slate-100 border-slate-200 text-slate-600">
              {healthCounts.unknown} ohne Buchungen
            </span>
          )}
        </div>
      )}

      {/* Vorschläge */}
      {proposals.length > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-md p-4 space-y-3" data-testid="proposals-section">
          <div className="flex items-center gap-2 font-semibold text-blue-900">
            <Lightning size={18} weight="fill" /> Erkannte Muster ({proposals.length})
            {scanCount !== null && <span className="ml-auto text-xs font-normal">aus {scanCount} Buchungen</span>}
          </div>
          <div className="space-y-2">
            {proposals.map((p, i) => (
              <div key={i} className="bg-white border border-blue-200 rounded p-3 flex items-center gap-3" data-testid={`proposal-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{p.suggested_name}</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {p.count}× {FREQ_LABEL[p.frequency]} · Ø {formatEUR(p.expected_amount)}
                    {p.counterparty_iban && <span className="ml-2 font-mono">{p.counterparty_iban}</span>}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Zeitraum: {p.months_seen[0]} → {p.months_seen[p.months_seen.length - 1]}
                  </div>
                </div>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1 h-8" onClick={() => setCreatingFromProposal(p)} data-testid={`proposal-accept-${i}`}>
                  <CheckCircle size={12} /> Plan erstellen
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suche + Liste */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Suchen…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="plans-search" />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} von {plans.length}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="plans-empty">
            <Receipt size={32} className="mx-auto text-slate-300 mb-2" />
            {plans.length === 0 ? (
              <div className="space-y-3">
                <div>
                  Noch keine Abschlagspläne angelegt.
                  {proposals.length > 0 && (
                    <span className="block mt-1 text-emerald-700 font-medium">
                      Oben findest du {proposals.length} erkannte{proposals.length === 1 ? "n" : ""} Vorschlag{proposals.length === 1 ? "" : "e"} — einfach übernehmen.
                    </span>
                  )}
                </div>
                {initialLoad ? (
                  <div className="text-xs text-slate-400">Scanne Bankbuchungen …</div>
                ) : proposals.length === 0 && scanCount !== null ? (
                  <div className="text-xs text-slate-500">
                    {scanCount} Buchungen gescannt — noch keine wiederkehrenden Muster gefunden.
                    <br />Sobald 3+ ähnliche Buchungen (Versorger oder Mieter-Zahlungen) vorliegen, tauchen sie hier auf.
                  </div>
                ) : null}
              </div>
            ) : "Kein Treffer für die Suche."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Name</th>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Typ</th>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Objekt</th>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Auftraggeber</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Betrag</th>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Frequenz</th>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600">Buchungen</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => {
                const prop = properties.find(x => x.id === p.property_id);
                const health = computeHealth(p);
                return (
                  <tr key={p.id} className="hover:bg-slate-50/40" data-testid={`plan-row-${p.id}`}>
                    <td className="px-3 py-2 font-medium">
                      {p.name}
                      {!p.active && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">inaktiv</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${health.cls}`}
                        data-testid={`plan-health-${p.id}`}
                        title={p.last_booking_date ? `Letzte Zahlung: ${formatDate(p.last_booking_date)}` : "Noch keine Buchung zugeordnet"}
                      >
                        {health.key === "ok" && <CheckCircle size={10} weight="fill" />}
                        {health.key === "due" && <Warning size={10} weight="fill" />}
                        {health.key === "overdue" && <Warning size={10} weight="fill" />}
                        {health.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{KIND_LABEL[p.kind]}</td>
                    <td className="px-3 py-2 text-xs">{prop?.name || (p.property_id ? "?" : "—")}</td>
                    <td className="px-3 py-2 text-xs">{p.counterparty_name || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEUR(p.expected_amount)}</td>
                    <td className="px-3 py-2 text-xs">{FREQ_LABEL[p.frequency]}</td>
                    <td className="px-3 py-2 text-xs tabular-nums">
                      {p.tx_count || 0}
                      {p.last_booking_date && <span className="block text-[10px] text-slate-500">letzte: {formatDate(p.last_booking_date)}</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`plan-edit-${p.id}`}>
                        <PencilSimple size={12} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50" onClick={() => deletePlan(p.id)} data-testid={`plan-delete-${p.id}`}>
                        <Trash size={12} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Neuer Plan / Bearbeiten */}
      <PlanDialog
        open={creating || !!editing || !!creatingFromProposal}
        onOpenChange={(v) => { if (!v) { setCreating(false); setEditing(null); setCreatingFromProposal(null); } }}
        plan={editing}
        proposal={creatingFromProposal}
        properties={properties}
        onSaved={() => { setCreating(false); setEditing(null); setCreatingFromProposal(null); setProposals(proposals.filter(p => p !== creatingFromProposal)); load(); }}
      />
    </div>
  );
}

function PlanDialog({ open, onOpenChange, plan, proposal, properties, onSaved }) {
  const [form, setForm] = useState(() => defaultForm(plan, proposal));
  const [saving, setSaving] = useState(false);
  const [units, setUnits] = useState([]);

  useEffect(() => { setForm(defaultForm(plan, proposal)); }, [plan, proposal, open]);

  useEffect(() => {
    if (!form.property_id) { setUnits([]); return; }
    api.get(`/properties/${form.property_id}/units`).then(r => setUnits(r.data || [])).catch(() => setUnits([]));
  }, [form.property_id]);

  const submit = async () => {
    setSaving(true);
    try {
      if (proposal) {
        await api.post("/installment-plans/from-proposal", {
          ...proposal,
          name: form.name,
          kind: form.kind,
          property_id: form.property_id || null,
          unit_ids: form.unit_ids,
          notes: form.notes,
        });
        toast.success(`Abschlagsplan angelegt · ${proposal.tx_ids?.length || 0} Buchungen verknüpft`);
      } else if (plan) {
        await api.put(`/installment-plans/${plan.id}`, {
          ...form,
          expected_amount: Number(form.expected_amount) || 0,
          tolerance_pct: Number(form.tolerance_pct) || 5,
        });
        toast.success("Aktualisiert");
      } else {
        await api.post("/installment-plans", {
          ...form,
          expected_amount: Number(form.expected_amount) || 0,
          tolerance_pct: Number(form.tolerance_pct) || 5,
        });
        toast.success("Angelegt");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="plan-dialog">
        <DialogHeader><DialogTitle>{plan ? "Abschlagsplan bearbeiten" : proposal ? "Vorschlag übernehmen" : "Neuer Abschlagsplan"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" />
          </div>
          <div>
            <Label className="text-xs">Typ</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="plan-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="utility">{KIND_LABEL.utility}</SelectItem>
                <SelectItem value="tenant_advance">{KIND_LABEL.tenant_advance}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Frequenz</Label>
            <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
              <SelectTrigger data-testid="plan-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">{FREQ_LABEL.monthly}</SelectItem>
                <SelectItem value="quarterly">{FREQ_LABEL.quarterly}</SelectItem>
                <SelectItem value="yearly">{FREQ_LABEL.yearly}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Erwarteter Betrag €</Label>
            <Input type="number" step="0.01" value={form.expected_amount} onChange={(e) => setForm({ ...form, expected_amount: e.target.value })} data-testid="plan-amount" />
          </div>
          <div>
            <Label className="text-xs">Toleranz %</Label>
            <Input type="number" step="0.1" value={form.tolerance_pct} onChange={(e) => setForm({ ...form, tolerance_pct: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Auftraggeber-Name</Label>
            <Input value={form.counterparty_name || ""} onChange={(e) => setForm({ ...form, counterparty_name: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Auftraggeber-IBAN</Label>
            <Input value={form.counterparty_iban || ""} onChange={(e) => setForm({ ...form, counterparty_iban: e.target.value.toUpperCase() })} className="font-mono text-xs" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Objekt</Label>
            <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? null : v, unit_ids: [] })}>
              <SelectTrigger data-testid="plan-property"><SelectValue placeholder="— Objekt wählen —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— kein Objekt (nur allgemein) —</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.property_number || p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.property_id && units.length > 0 && (
            <div className="col-span-2">
              <Label className="text-xs">Einheiten (optional — leer = ganzes Objekt)</Label>
              <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-2 space-y-1 mt-1">
                {units.map(u => (
                  <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 p-1 rounded" data-testid={`plan-unit-${u.id}`}>
                    <input type="checkbox" checked={form.unit_ids.includes(u.id)}
                      onChange={(e) => setForm({ ...form, unit_ids: e.target.checked ? [...form.unit_ids, u.id] : form.unit_ids.filter(x => x !== u.id) })} />
                    <span className="font-mono">{u.unit_number}</span>
                    {u.location && <span className="text-slate-500">· {u.location}</span>}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="col-span-2">
            <Label className="text-xs">Verwendungszweck-Muster (optional)</Label>
            <Input value={form.purpose_pattern || ""} onChange={(e) => setForm({ ...form, purpose_pattern: e.target.value })} placeholder='z. B. "Strom" oder "Abschlag"' />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Notizen</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={submit} disabled={saving || !form.name || !form.expected_amount} data-testid="plan-submit">
            {saving ? "Speichere…" : proposal ? "Plan anlegen & verknüpfen" : plan ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultForm(plan, proposal) {
  if (plan) return { ...plan, unit_ids: plan.unit_ids || [] };
  if (proposal) return {
    name: proposal.suggested_name || "",
    kind: proposal.kind || "utility",
    frequency: proposal.frequency || "monthly",
    expected_amount: proposal.expected_amount || 0,
    tolerance_pct: 5,
    counterparty_name: proposal.counterparty_name || "",
    counterparty_iban: proposal.counterparty_iban || "",
    purpose_pattern: proposal.purpose_pattern || "",
    property_id: null,
    unit_ids: [],
    notes: "",
    active: true,
  };
  return {
    name: "", kind: "utility", frequency: "monthly",
    expected_amount: "", tolerance_pct: 5,
    counterparty_name: "", counterparty_iban: "",
    purpose_pattern: "", property_id: null, unit_ids: [],
    notes: "", active: true,
  };
}
